import { useState } from "react";
import type { MeResponse } from "../../shared/api.js";
import { api } from "../lib/api.js";
import { Button } from "../components/Button.js";
import { Field } from "../components/Field.js";

export function HouseholdScreen({
  me,
}: {
  me: MeResponse;
}) {
  const isOwnerRole = me.group.members.find((m) => m.userId === me.user.id)?.role === "owner";

  const [recipient, setRecipient] = useState("");
  const [invite, setInvite] = useState<{ code: string; link: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function createInvite(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.createInvite(me.group.id, {
        operationId: crypto.randomUUID(),
        ...(recipient.trim() ? { recipientEmail: recipient.trim() } : {}),
        maxUses: 1,
      });
      setInvite({ code: res.invite.code, link: res.link });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create invite.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h1 className="font-display text-[28px] text-granite-900 dark:text-granite-50 mb-1">
          {me.group.name}
        </h1>
        <p className="font-text text-granite-600 dark:text-granite-300">
          {me.group.members.length} {me.group.members.length === 1 ? "person" : "people"}
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <p className="font-text text-[13px] uppercase tracking-wider text-granite-500 dark:text-granite-400">
          Members
        </p>
        <div className="panel divide-y divide-granite-100 dark:divide-granite-700">
          {me.group.members.map((m) => (
            <div key={m.userId} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-granite-200 dark:bg-granite-700 text-granite-700 dark:text-granite-200 font-text text-[14px]">
                  {m.displayName.charAt(0).toUpperCase()}
                </span>
                <span className="font-text text-granite-800 dark:text-granite-100">
                  {m.displayName}
                  {m.userId === me.user.id ? " (you)" : ""}
                </span>
              </div>
              <span className="font-text text-[13px] text-granite-500 dark:text-granite-400">
                {m.role === "owner" ? "Organiser" : "Member"}
              </span>
            </div>
          ))}
        </div>
      </section>

      {isOwnerRole && (
        <section className="panel p-4 flex flex-col gap-3">
          <h2 className="font-text font-semibold text-[17px] text-granite-800 dark:text-granite-100">
            Invite someone
          </h2>
          <form onSubmit={createInvite} className="flex flex-col gap-3">
            <Field
              label="Their email (optional)"
              hint="Leaving blank lets anyone with the code join."
              type="email"
              inputMode="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
            />
            {error && <p className="font-text text-blaze text-[15px]" role="alert">{error}</p>}
            <Button type="submit" disabled={busy}>
              {busy ? "Creating…" : "Create invite"}
            </Button>
          </form>
          {invite && (
            <div className="mt-2 p-3 rounded-[var(--radius-control)] border border-granite-300 dark:border-granite-600 flex flex-col gap-2">
              <p className="font-text text-[15px] text-granite-700 dark:text-granite-200">
                Share this link (single use):
              </p>
              <code className="font-data text-[13px] break-all text-granite-600 dark:text-granite-300">
                {invite.link}
              </code>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
